import { ArrowSquareOutIcon } from "@phosphor-icons/react/dist/csr/ArrowSquareOut";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import type { ApAttachment, ApInvoice } from "@/accounts-payable/types";
import { fileKind, formatFileSize } from "@/accounts-payable/utils";
import { useApiFileHref } from "@/core/api/api-file-href";
import { ExtractedInvoiceRow } from "./extracted-invoice-row";
import {
  fileIconFor,
  missingInvoicesMessage,
  NO_INVOICE_OUTCOME,
} from "./utils";

const Note = ({
  children,
  negative = false,
}: {
  children: string;
  negative?: boolean;
}) => (
  // Same vertical padding as an invoice row, so a note lines up the same way.
  <Text
    variant="caption"
    tone={negative ? "destructive" : "muted"}
    className="py-2"
  >
    {children}
  </Text>
);

/**
 * One email attachment: a file icon and name, with a link to open the stored
 * file (whatever the outcome, so a skipped or failed file can be checked by
 * eye), then (indented on a thin rule, to read as coming *from* the file) the
 * invoices extracted from it (one attachment can yield several, e.g. a
 * statement), or a line saying why there are none.
 */
export const EmailAttachmentRow = ({
  attachment,
  invoices,
  invoicesLoading,
  receiptsEnabled = true,
}: {
  attachment: ApAttachment;
  /** Every invoice loaded for the email; filtered to this attachment here. */
  invoices: ApInvoice[];
  invoicesLoading: boolean;
  /** Whether the business processes sales receipts (see `useReceiptsEnabled`). */
  receiptsEnabled?: boolean;
}) => {
  const matchedInvoices = invoices.filter((i) =>
    attachment.invoice_ids.includes(i.id),
  );
  const expected = attachment.invoice_ids.length;
  const outcome =
    attachment.status === "is_receipt" && !receiptsEnabled
      ? { text: "A sales receipt. Receipts are turned off for this business." }
      : NO_INVOICE_OUTCOME[attachment.status];
  const Icon = fileIconFor(attachment.content_type);
  const missingMessage = missingInvoicesMessage(
    expected,
    matchedInvoices.length,
    invoicesLoading,
  );
  // Some files arrive without a size; show just the type then, not "· —".
  const size = attachment.file_size
    ? ` · ${formatFileSize(attachment.file_size)}`
    : "";
  const fileHref = useApiFileHref(attachment.file_url);

  return (
    <Stack className="w-full" direction="row" align="start" gap={2}>
      <Icon
        className="h-5 w-5 flex-none text-subtle"
        strokeWidth={1.5}
        aria-hidden
      />
      <Stack align="start" gap={2} className="w-full min-w-0">
        <Stack
          direction="row"
          align="start"
          justify="between"
          gap={3}
          className="w-full"
        >
          <Stack className="min-w-0" align="start" gap={0}>
            <Text variant="label" className="break-all">
              {attachment.file_name}
            </Text>
            <Text variant="caption" tone="muted">
              {fileKind(attachment.content_type)}
              {size}
            </Text>
          </Stack>
          {/* No link when nothing was stored, e.g. a failed download. A type
              the browser can't show (a spreadsheet, say) downloads instead. */}
          {fileHref && (
            <a
              href={fileHref}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Open ${attachment.file_name} in a new tab`}
              className="inline-flex flex-none items-center gap-1 text-sm font-medium text-link hover:underline focus-visible:ring-2 focus-visible:ring-focus focus-visible:outline-hidden"
            >
              Open
              <ArrowSquareOutIcon className="size-3" aria-hidden />
            </a>
          )}
        </Stack>

        {/* align="stretch" so each invoice row spans the width (amount on the
            right, hover fill edge to edge) instead of shrink-wrapping. */}
        <Stack
          gap={0}
          align="stretch"
          className="w-full border-l-2 border-line pl-3"
        >
          {/* Branch on invoice_ids, not the fetch results: "Not an invoice"
              is about classification and must never show for an attachment
              that produced invoices. */}
          {expected === 0 ? (
            <Note negative={outcome.negative}>{outcome.text}</Note>
          ) : (
            <>
              {matchedInvoices.map((invoice) => (
                <ExtractedInvoiceRow key={invoice.id} invoice={invoice} />
              ))}
              {missingMessage && <Note>{missingMessage}</Note>}
            </>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
